import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { searchMovies, searchPeople, getMoviesForPerson, searchKeywords, discoverByKeywords } from '../lib/tmdb'
import Logo from '../components/Logo'
import Page from '../components/Page'
import TabBar from '../components/TabBar'

export default function AddMovie() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [people, setPeople] = useState([])
  const [keywords, setKeywords] = useState([])
  const [selectedPerson, setSelectedPerson] = useState(null)
  const [selectedKeyword, setSelectedKeyword] = useState(null)
  const [searching, setSearching] = useState(false)
  const debounceRef = useRef(null)
  const personLoadedRef = useRef(false)

  // If navigated here with a person from MovieDetail cast, auto-load their filmography
  useEffect(() => {
    if (location.state?.person && !personLoadedRef.current) {
      personLoadedRef.current = true
      handleSelectPerson(location.state.person)
    }
  }, [location.state])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (query.length < 2) { setResults([]); setPeople([]); setKeywords([]); return }
    if (selectedPerson || selectedKeyword) return

    debounceRef.current = setTimeout(async () => {
      setSearching(true)
      const [movies, found, kws] = await Promise.all([
        searchMovies(query),
        searchPeople(query),
        searchKeywords(query),
      ])
      const q = query.toLowerCase()
      const relevant = found.filter(p => p.name.toLowerCase().includes(q)).slice(0, 3)
      setPeople(relevant)
      setKeywords(kws.slice(0, 4))
      setResults(movies)
      setSearching(false)
    }, 350)

    return () => clearTimeout(debounceRef.current)
  }, [query, selectedPerson, selectedKeyword])

  async function handleSelectPerson(person) {
    setSelectedPerson(person)
    setSelectedKeyword(null)
    setSearching(true)
    const movies = await getMoviesForPerson(person.id)
    setResults(movies)
    setPeople([])
    setKeywords([])
    setSearching(false)
  }

  async function handleSelectKeyword(kw) {
    setSelectedKeyword(kw)
    setSelectedPerson(null)
    setSearching(true)
    const movies = await discoverByKeywords([kw.id])
    setResults(movies)
    setPeople([])
    setKeywords([])
    setSearching(false)
  }

  function clearSelection() {
    setSelectedPerson(null)
    setSelectedKeyword(null)
    setResults([])
  }

  function handleSelect(movie) {
    navigate(`/movie/${movie.tmdb_id}`)
  }

  return (
    <Page mode="dashboard">
      {/* Header */}
      <div className="flex items-center justify-between py-4 mb-10">
        <Logo size="md" />
        <button
          onClick={() => navigate('/watchlist')}
          className="text-sm font-semibold text-[var(--fp-red)] bg-transparent border-none cursor-pointer"
        >
          Cancel
        </button>
      </div>

      <h1 className="fp-display text-3xl text-white mb-10">Add to Watchlist</h1>

      {/* Selected person banner (filmography mode) */}
      {selectedPerson && (
        <div className="fp-card-elevated flex items-center gap-5 mb-10">
          <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 bg-[var(--fp-border)]">
            {selectedPerson.profile_url ? (
              <img src={selectedPerson.profile_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-2xl">⭐</div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-base font-semibold text-white truncate">{selectedPerson.name}</div>
            <div className="text-sm text-[var(--fp-text-muted)] mt-1">Showing filmography</div>
          </div>
          <button onClick={clearSelection} className="text-sm font-semibold text-[var(--fp-red)] bg-transparent border-none cursor-pointer">
            Clear
          </button>
        </div>
      )}

      {/* Selected keyword/theme banner */}
      {selectedKeyword && (
        <div className="fp-card-elevated flex items-center gap-5 mb-10">
          <div className="w-14 h-14 rounded-full flex-shrink-0 bg-[var(--fp-gold-soft)] flex items-center justify-center text-2xl">
            🏷️
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-base font-semibold text-white truncate capitalize">{selectedKeyword.name}</div>
            <div className="text-sm text-[var(--fp-text-muted)] mt-1">Movies with this theme</div>
          </div>
          <button onClick={clearSelection} className="text-sm font-semibold text-[var(--fp-red)] bg-transparent border-none cursor-pointer">
            Clear
          </button>
        </div>
      )}

      {!selectedPerson && !selectedKeyword && (
        <div className="relative mb-10">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg text-[var(--fp-text-dim)]">🔍</span>
          <input
            type="text"
            placeholder="Search movies or actors…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="fp-input pl-12 text-base"
            style={{ padding: '18px 18px 18px 48px', fontSize: '16px' }}
            autoFocus
          />
        </div>
      )}

      {searching && (
        <p className="text-base text-[var(--fp-text-muted)] text-center py-8">Searching...</p>
      )}

      {people.length > 0 && (
        <div className="mb-10">
          <div className="text-[11px] fp-gold-text uppercase tracking-[0.25em] font-semibold mb-5 pl-2">
            People — tap to see their movies
          </div>
          <div className="fp-card overflow-hidden">
            {people.map(p => (
              <button
                key={p.id}
                onClick={() => handleSelectPerson(p)}
                className="w-full flex gap-5 py-5 px-2 border-b border-[var(--fp-border)] last:border-b-0 first:pt-0 last:pb-0 bg-transparent text-left cursor-pointer hover:bg-[var(--fp-elevated)] transition-colors"
              >
                <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 bg-[var(--fp-border)]">
                  {p.profile_url ? (
                    <img src={p.profile_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-2xl">⭐</div>
                  )}
                </div>
                <div className="flex-1 min-w-0 flex flex-col justify-center">
                  <div className="text-base font-semibold text-white truncate">{p.name}</div>
                  <div className="text-sm text-[var(--fp-text-muted)] mt-1 truncate">
                    {p.known_for || 'Actor'}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Theme/keyword suggestions */}
      {keywords.length > 0 && (
        <div className="mb-10">
          <div className="text-[11px] fp-gold-text uppercase tracking-[0.25em] font-semibold mb-5 pl-2">
            Themes — tap to browse movies
          </div>
          <div className="flex flex-wrap gap-3">
            {keywords.map(kw => (
              <button
                key={kw.id}
                onClick={() => handleSelectKeyword(kw)}
                className="px-4 py-2.5 rounded-full text-sm font-semibold border cursor-pointer hover:border-[var(--fp-gold)] hover:text-white transition-colors capitalize"
                style={{
                  background: 'var(--fp-surface)',
                  borderColor: 'var(--fp-border-accent)',
                  color: 'var(--fp-text-muted)',
                }}
              >
                🏷️ {kw.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {results.length > 0 && !selectedPerson && !selectedKeyword && (
        <div className="text-[11px] fp-gold-text uppercase tracking-[0.25em] font-semibold mb-5 pl-2">
          Movies
        </div>
      )}

      {results.length > 0 && (
        <div className="fp-card overflow-hidden">
          {results.map(movie => (
            <button
              key={movie.tmdb_id}
              onClick={() => handleSelect(movie)}
              className="w-full flex gap-5 py-5 px-2 border-b border-[var(--fp-border)] last:border-b-0 first:pt-0 last:pb-0 bg-transparent text-left cursor-pointer hover:bg-[var(--fp-elevated)] transition-colors"
            >
              <div className="w-16 h-24 rounded-xl overflow-hidden flex-shrink-0 bg-[var(--fp-border)] shadow-md">
                {movie.poster_url ? (
                  <img src={movie.poster_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-2xl bg-[var(--fp-surface)]">🎬</div>
                )}
              </div>
              <div className="flex-1 min-w-0 flex flex-col justify-center">
                <div className="fp-display text-base text-white truncate">{movie.title}</div>
                <div className="text-sm text-[var(--fp-text-muted)] mt-2">
                  {[movie.year, movie.genre].filter(Boolean).join(' · ')}
                </div>
              </div>
              <span className="text-sm text-[var(--fp-text-dim)] self-center">→</span>
            </button>
          ))}
        </div>
      )}

      {query.length >= 2 && !searching && results.length === 0 && people.length === 0 && keywords.length === 0 && !selectedPerson && !selectedKeyword && (
        <p className="text-base text-[var(--fp-text-muted)] text-center py-12">
          Nothing found for "{query}"
        </p>
      )}

      <TabBar />
    </Page>
  )
}
